/**
 * Top bar messages (sections/top-bar.liquid), Oct 2026.
 *
 * Scrolls the messages only when they overflow their space AND the toggle for the current breakpoint is on
 * (data-scroll-desktop ≥1200px, data-scroll-mobile ≤1199px) AND the visitor allows motion. Otherwise they sit
 * still: centered when they fit, clipped with an edge fade when they don't. Re-checks on resize.
 *
 * The loop is a CSS transform animation (component-top-bar.css). For a seamless loop the message list is
 * cloned once; the copy is aria-hidden and inert so screen readers and keyboard users meet each message once.
 * Measurement happens only from ResizeObserver / media-query changes, never per frame.
 */
if (!customElements.get('top-bar-messages')) {
  class TopBarMessages extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.top-bar__viewport');
      this.track = this.querySelector('.top-bar__track');
      this.group = this.querySelector('.top-bar__group');
      if (!this.viewport || !this.track || !this.group) return;

      this.speed = parseFloat(this.dataset.speed) || 40;
      this.desktopQuery = window.matchMedia('(min-width: 1200px)');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.scheduleUpdate = this.scheduleUpdate.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);

      this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
      this.resizeObserver.observe(this.viewport);
      this.resizeObserver.observe(this.group);
      this.desktopQuery.addEventListener('change', this.scheduleUpdate);
      this.motionQuery.addEventListener('change', this.scheduleUpdate);
      this.addEventListener('pointerup', this.onPointerUp);
      this.addEventListener('focusout', this.onFocusOut);

      this.scheduleUpdate();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.scheduleUpdate);
      if (this.motionQuery) this.motionQuery.removeEventListener('change', this.scheduleUpdate);
      this.removeEventListener('pointerup', this.onPointerUp);
      this.removeEventListener('focusout', this.onFocusOut);
      cancelAnimationFrame(this.frame);
    }

    scheduleUpdate() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }

    update() {
      const overflowing = this.group.scrollWidth > this.viewport.clientWidth + 1;
      const toggleOn = this.desktopQuery.matches ? this.hasAttribute('data-scroll-desktop') : this.hasAttribute('data-scroll-mobile');
      const scroll = overflowing && toggleOn && !this.motionQuery.matches;

      this.classList.toggle('is-overflowing', overflowing);

      if (scroll) {
        if (!this.clone) {
          this.clone = this.group.cloneNode(true);
          this.clone.setAttribute('aria-hidden', 'true');
          this.clone.setAttribute('inert', '');
          this.clone.querySelectorAll('a, button, [tabindex]').forEach((el) => el.setAttribute('tabindex', '-1'));
          this.clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
          this.track.appendChild(this.clone);
        }
        this.classList.add('is-scrolling');
        // Distance from the first list to its copy (list width + the divider gap) = one seamless cycle
        const distance = this.clone.offsetLeft - this.group.offsetLeft;
        this.style.setProperty('--top-bar-distance', `${distance}px`);
        this.style.setProperty('--top-bar-duration', `${(distance / this.speed).toFixed(2)}s`);
      } else {
        this.classList.remove('is-scrolling', 'is-paused');
        if (this.clone) {
          this.clone.remove();
          this.clone = null;
        }
      }
    }

    // Tap toggles pause on touch; taps on a link still follow it
    onPointerUp(event) {
      if (event.pointerType === 'mouse' || !this.classList.contains('is-scrolling')) return;
      if (event.target.closest('a')) return;
      this.classList.toggle('is-paused');
    }

    // Focusing a clipped link scrolls the viewport to it; put it back once focus leaves the messages
    onFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;
      this.viewport.scrollLeft = 0;
    }
  }

  customElements.define('top-bar-messages', TopBarMessages);
}
